import {
  AirlineCode,
  AirwayWithCheapestFlightsItemType,
  FlightOriginType,
  Order,
  OrderAdvanceItem,
  OrderAdvanceItemType,
  OrderAirwayItem,
  OrderAirwayItemType,
  OrderApprovalModel,
  OrderApprover,
  OrderDestinations,
  OrderHotelItem,
  OrderHotelItemType,
  OrderHotelOfflineItem,
  OrderItems,
  OrderRoadItem,
  OrderRoadItemType,
  OrderVehicleItem,
  OrderVehicleItemType,
  ViolatedPolicyOrder,
  PixKeyType,
  DuplicityCheckResult,
  HotelSuggestion,
} from "~/application/types";
import { DateUtils } from "~/application/utils";
import { toDate } from "~/utils/date.utils";
import {
  AirwayWithCheapestFlightsItemTypeDTO,
  OrderAdvanceItemTypeDTO,
  OrderAirwayItemTypeDTO,
  OrderApprovalModelDTO,
  OrderDestinationsDTO,
  OrderDTO,
  OrderHotelItemTypeDTO,
  OrderHotelOfflineItemDTO,
  OrderRoadItemTypeDTO,
  OrderVehicleItemTypeDTO,
  ViolatedPolicyOrderDTO,
  DuplicityCheckDTO,
} from "../dtos";
import { mapFlightOptionDTO } from "./mapFlightOptionDTO";
import { mapFlightSegmentDTO } from "./mapFlightSegmentDTO";
import { mapReasonTripDTO } from "./mapReasonTripDTO";
import { mapTravelerDTO } from "./mapTravelerDTO";
import { mapVehicleDetailDTO } from "./mapVehicleDTO";
import { mapViolatedPoliciesDTO } from "./mapViolatedPoliciesDTO";
import { mapUnflownAirway } from "./mapUnflownAirway";
import { mapHotelRuleDTO } from "./mapHotelRuleDTO";
import slugify from "slugify";
import { mapAgreementDTO } from "./mapAgreementDTO";
import { mapVehicleCoverageDTO } from "./mapVehicleCoverageDTO";
import { mapOrderExpenseReportItemTypeDTO } from "./mapExpenseReportItemTypeDTO";
import { mapProviderDTO } from "./mapProviderDTO";
import { mapExtraOrderCostDTO } from "./mapExtraOrderCostDTO";
import { mapClaraCardDTO } from "./mapClaraCardDTO";
import { mapExpenseCardDTO } from "./mapExpenseCardDTO";
import { mapCardTransactionDTO } from "./mapCardTransactionDTO";
import { mapBranchDTO } from "./mapBranchDTO";
import { mapHotelSuggestionWithFallbackDTO } from "./mapHotelSuggestionDTO";

export function mapOrderAirwayItemTypeDTO(
  data?: OrderAirwayItemTypeDTO,
): OrderAirwayItemType | null {
  if (!data) return null;

  return {
    flights: data.flights.map(
      (item): OrderAirwayItem => ({
        uuid: item.uuid,
        value: item.value,
        codeshare: item.codeshare,
        credential: item.credential,
        flightType: item.flight_type,
        isInternational: item.is_international,
        baggages: item?.baggages,
        arrivalAirportIata: item?.arrival_airport_iata,
        departureAirportIata: item?.departure_airport_iata,

        passengers: item.passengers.map((traveler) => ({
          ...mapTravelerDTO(traveler),
          amountLuggage: traveler.amount_luggage,
          ticket: traveler.ticket,
          status: traveler.status,
          unflownAirway: traveler.unflown_ticket
            ? mapUnflownAirway(traveler.unflown_ticket)
            : undefined,
        })),
        availableSeatMap: item.available_seat_map,
        isTheCheapestFlight: item.is_the_cheapest_flight,
        servicesIncluded: item.services_included
          ? {
              baggage: item.services_included.baggage,
              checkedBaggage: item.services_included.checked_baggage,
              comfortSeat: item.services_included.comfort_seat,
              refund: item.services_included.refund,
              refundDescription: item.services_included.refund_description,
              rescheduling: item.services_included.rescheduling,
              seatAssignment: item.services_included.seat_assignment,
            }
          : undefined,
        provider: item.provider,
        segments: item.segments.map((seg) => ({
          airline: slugify(seg.airline.toString()) as AirlineCode,
          type: seg.type,
          arrivalFlightInfo: {
            shortAirport: seg.arrivalInfo.short_airport,
            airportIata: seg.arrivalInfo.airport_iata,
            airport: seg.arrivalInfo.airport,
            dateTime: DateUtils.toDate(seg.arrivalInfo.dateTime),
          },
          departureFlightInfo: {
            shortAirport: seg.departureInfo.short_airport,
            airportIata: seg.departureInfo.airport_iata,
            airport: seg.departureInfo.airport,
            dateTime: DateUtils.toDate(seg.departureInfo.dateTime),
          },
          flightNumber: seg.flightNumber,
        })),
        familyFare: item.family_fare,
        status: item.status,
        departureAirport: item.departure_airport,
        arrivalAirport: item.arrival_airport,
        createdAt: DateUtils.toDate(item.created_at),
        reasonRejected: item.reason_rejected,
        expiresAt: toDate(item.expires_at),
        tracker: item.tracker ?? undefined,
        markdown: item.markdown,
        du: item.du,
        boardingTax: item.boarding_tax,
        fee: item.fee,
        providerValue: item.provider_value,
        markup: item.markup,
        fare: item.fare,
        changeTax: parseFloat(item.change_tax),
        violatedPolicies: mapViolatedPoliciesDTO(item.violated_policies),
        policyViolationComplement: item.policy_violation_complement,
        policyViolationJustification: item.policy_violation_justification,
        justificationAttachmentUrl: item.justification_attachment_url,
      }),
    ),
    value: data.value,
    fees: {
      fee: data.fees?.fee || 0,
      boardingTax: data.fees?.boarding_tax,
      seatValue: data.fees?.seat_value,
    },
  };
}

export function mapAirwayWithCheapestFlightsItemDTO(
  data: AirwayWithCheapestFlightsItemTypeDTO,
): AirwayWithCheapestFlightsItemType {
  const { airway_order, cheapest_flights } = data;
  return {
    airwayOrder: {
      id: airway_order.id,
      uuid: airway_order.uuid,
      airline: airway_order.airline,
      codeshare: airway_order.codeshare,
      departureAirport: airway_order.departure_airport,
      arrivalAirport: airway_order.arrival_airport,
      departureDate: toDate(airway_order.departure_date),
      arrivalDate: toDate(airway_order.arrival_date),
      baggages: airway_order.baggages,
      servicesIncluded: airway_order.services_included
        ? {
            baggage: airway_order.services_included.baggage,
            checkedBaggage: airway_order.services_included.checked_baggage,
            comfortSeat: airway_order.services_included.comfort_seat,
            refund: airway_order.services_included.refund,
            refundDescription: airway_order.services_included.refund_description,
            rescheduling: airway_order.services_included.rescheduling,
            seatAssignment: airway_order.services_included.seat_assignment,
          }
        : undefined,
      segments: airway_order.segments.map?.((segment) => ({
        airline: airway_order.airline,
        flightNumber: segment.flightNumber,
        departureFlightInfo: {
          shortAirport: segment.departureInfo.short_airport,
          airportIata: segment.departureInfo.airport_iata,
          airport: segment.departureInfo.airport,
          dateTime: toDate(segment.departureInfo.dateTime),
        },
        arrivalFlightInfo: {
          shortAirport: segment.arrivalInfo.short_airport,
          airportIata: segment.arrivalInfo.airport_iata,
          airport: segment.arrivalInfo.airport,
          dateTime: toDate(segment.arrivalInfo.dateTime),
        },
      })),
      availableSeatMap: airway_order.avaliabel_seat_map,
      isTheCheapestFlight: airway_order.is_the_cheapest_flight,
      familyFare: airway_order.family_fare,
      flightNumber: airway_order.flight_number,
      flightType: airway_order.flight_type,
      value: parseFloat(airway_order.value),
      fare: airway_order.fare,
      boardingTax: airway_order.boarding_tax,
      fee: airway_order.fee,
      tracker: airway_order.tracker,
      expiresAt: toDate(airway_order.expires_at),
      ticket: airway_order.ticket,
      passengers: airway_order.passengers.map?.((passenger) => ({
        uuid: passenger.uuid,
        fullName: passenger.full_name,
        dateBirth: passenger.date_birth,
        amountLuggage: passenger.amount_luggage,
        ticket: passenger.ticket,
        position: passenger.position,
      })),
      justification: airway_order.justification,
      reasonRejected: airway_order.reason_rejected,
      isRejected: Boolean(airway_order.rejected),
      violatedPolicies: mapViolatedPoliciesDTO(airway_order.violated_policies),
    },
    cheapestFlights: cheapest_flights
      ? cheapest_flights.map((item) => {
          const hasDiscount = item.discount && !Array.isArray(item.discount);

          return {
            id: item.id,
            airline: item.airline,
            scales: item.scales,
            hash: item.hash,
            baggages: item.baggages,
            flightOrigin: item.flightOrigin.toLowerCase() as FlightOriginType,
            segments: item.segments.map(mapFlightSegmentDTO),
            minimumPrice: item.minimumPrice,
            discount: hasDiscount && item.discount,
            options: item.options.map(mapFlightOptionDTO),
            violatedPolicies: mapViolatedPoliciesDTO(item.violated_policies),
          };
        })
      : null,
  };
}

export function mapOrderRoadItemTypeDTO(data?: OrderRoadItemTypeDTO): OrderRoadItemType | null {
  if (!data) return null;

  const travels = data.travels.map(
    (item): OrderRoadItem => ({
      uuid: item.uuid,
      arrival: item.arrival,
      arrivalDate: DateUtils.toDate(item.arrival_date),
      companyImage: item.company_image,
      companyName: item.company_name,
      createdAt: DateUtils.toDate(item.created_at),
      credential: item.credential,
      insuranceValue: item.insurance_value,
      departure: item.departure,
      departureDate: DateUtils.toDate(item.departure_date),
      provider: item.provider ? mapProviderDTO(item.provider) : null,
      seatClass: item.seatClass,
      duration: parseFloat(item.duration),
      status: item.status,
      fee: item.fee,
      originalValue: item.original_value,
      value: item.value,
      fare: item.fare,
      isTheCheapestRoad: item.is_the_cheapest_road,
      tracker: item.tracker ?? undefined,
      travelers: item.travelers.map((traveler) => ({
        ...mapTravelerDTO(traveler),
        seats: traveler.seats.map((seat) => ({
          seatType: seat.seat_type,
          isConnection: seat.is_connection,
          seatNumber: seat.seat_number,
          ticket: seat.ticket,
          bpe: seat.bpe,
        })),
        status: traveler.status,
      })),
      connection: item.connection
        ? {
            name: item.connection?.name,
            seatClass: item.connection?.seat_class,
            departureDate: toDate(item.connection?.departure_date),
            arrivalDate: toDate(item.connection?.arrival_date),
            availableSeats: item.connection?.available_seats,
            bpe: item.connection?.bpe,
            companyName: item.connection?.company_name,
            companyUrl: item.connection?.company_url,
          }
        : undefined,
      connectionId: item.connection_uuid,
      reasonRejected: item.reason_rejected,
      bpe: item.bpe_ticket,
      ticket: item.ticket,
      markup: item.markup,
      providerFees: item.provider_fees,
      providerUnitFee: item.provider_unit_fee,
      cancellationTax: item.cancellation_tax,
      providerIdentification: item.provider_identification,
      violatedPolicies: mapViolatedPoliciesDTO(item.violated_policies),
      arrivalCity: item.arrival_city,
      departureCity: item.departure_city,
      policyViolationComplement: item.policy_violation_complement,
      policyViolationJustification: item.policy_violation_justification,
      justificationAttachmentUrl: item.justification_attachment_url,
    }),
  );

  travels.sort((a, b) => {
    const byDate = a.departureDate.getTime() - b.departureDate.getTime();
    if (byDate !== 0) return byDate;
    return (a.connectionId ?? "").localeCompare(b.connectionId ?? "");
  });

  return { travels, value: data.value, fees: data.fees };
}

export function mapOrderVehicleItemTypeDTO(
  data?: OrderVehicleItemTypeDTO,
): OrderVehicleItemType | null {
  if (!data) return null;

  return {
    vehicles: data.vehicles.map(
      (item): OrderVehicleItem => ({
        arrival: item.arrival,
        rentalCompany: item.rental_company,
        rentalCompanyImageUrl: item.rental_company_image_url,
        arrivalCity: item.arrival_city,
        arrivalDate: DateUtils.toDate(item.arrival_date),
        conductor: {
          ...mapTravelerDTO(item.conductor),
        },
        createdAt: DateUtils.toDate(item.created_at),
        credential: item.credential,
        departure: item.departure,
        departureCity: item.departure_city,
        departureDate: DateUtils.toDate(item.departure_date),
        provider: item.provider,
        status: item.status,
        uuid: item.uuid,
        vehicleCompanyImage: item.vehicle_company_image,
        vehicleDescription: item.vehicle_description,
        vehicleGroup: item.vehicle_group,
        vehicleImage: item.vehicle_image,
        tracker: item.tracker ?? undefined,
        vehicleDoorsNumbers: item.vehicle_doors_numbers,
        vehicleAirConditioner: item.vehicle_air_conditioner,
        vehicleTransmissionType: item.vehicle_transmission_type,
        reasonRejected: item.reason_rejected,
        detail: mapVehicleDetailDTO(item?.detail),
        violatedPolicies: mapViolatedPoliciesDTO(item.violated_policies),
        returnFeeAnotherCity: item.return_fee_another_city,
        policyViolationComplement: item.policy_violation_complement,
        policyViolationJustification: item.policy_violation_justification,
        justificationAttachmentUrl: item.justification_attachment_url,
        coverage: item.coverage ? mapVehicleCoverageDTO(item.coverage) : undefined,
        value: item.value,
        km: item.km,
        monthlyFeatures: item.monthly_features,
      }),
    ),
    value: data.value,
    fees: data.fees,
  };
}

export function mapOrderHotelObsOfflineDTO(
  obsOffline?: OrderHotelOfflineItemDTO,
): OrderHotelOfflineItem | undefined {
  if (!obsOffline) return undefined;

  const suggestedHotel = mapHotelSuggestionWithFallbackDTO(
    obsOffline.suggested_hotel,
    obsOffline.suggestion,
  );

  return {
    amenities: obsOffline.amenities,
    city: obsOffline.city,
    suggestion: suggestedHotel?.name || obsOffline.suggestion,
    obs: obsOffline.obs,
    regimen: obsOffline.regimen,
    suggestedHotel,
    suggestedHotelContact: obsOffline.suggested_hotel_contact,
    maxValue: obsOffline.max_value,
    isExtension: obsOffline.is_extension ?? obsOffline.isExtension,
    alreadyHosted: obsOffline.already_hosted ?? obsOffline.alreadyHosted,
  };
}

export function mapOrderHotelItemTypeDTO(data?: OrderHotelItemTypeDTO): OrderHotelItemType | null {
  if (!data) return null;

  return {
    rooms: (data.rooms || [])?.map(
      (item): OrderHotelItem => ({
        accommodation: item.accommodation,
        imageLink: item.image_link,
        checkIn: DateUtils.toDate(item.checkin_date),
        credentialDescription: item.credential_description,
        checkOut: DateUtils.toDate(item.checkout_date),
        cityName: item.city_name,
        createdAt: DateUtils.toDate(item.created_at),
        credential: item.credential,
        obs: item.obs,
        guests: item.guests.map((t) => ({
          ...mapTravelerDTO(t),
          age: t.age,
          passengerType: t.passenger_type,
          pixKey: t.pix_key,
        })),
        hotel: item.hotel,
        provider: item.provider,
        status: item.status,
        uuid: item.uuid,
        roomDescription: item.room_description,
        roomRegimen: item.room_regimen,
        value: item.value,
        fee: item.fee,
        markup: item.markup,
        agreement: item?.agreement ? mapAgreementDTO(item?.agreement) : undefined,
        approvedValue: item.approved_value,
        tracker: item.tracker ?? undefined,
        reasonRejected: item.reason_rejected,
        isOffline: item.is_offline,
        isFixedDaily: item.is_fixed_daily ?? false,
        dailyAmount: item.daily_amount,
        stars: item.stars,
        address: item.address,
        obsOffline: mapOrderHotelObsOfflineDTO(item.obs_offline),
        amenities: item.amenities,
        options: item?.options?.map((option) => ({
          uuid: option.uuid,
          description: option.description,
          address: option.address,
          phone: option.phone,
          email: option.email,
          roomType: option.room_type,
          regimen: option.regimen,
          city: option.city,
          providerValue: parseFloat(option.provider_value),
          customerValue: parseFloat(option.customer_value),
          hotelFee: parseFloat(option.hotel_fee),
          hotelMarkup: parseFloat(option.hotel_markup),
          paymentPix: option.payment_pix,
          paymentCreditCard: option.payment_credit_card,
          obsIssuer: option.obs_issuer,
          obsAgency: option.obs_agency,
          status: option.status,
          amenities: option.amenities?.map((amenity) => ({
            uuid: amenity.uuid,
            name: amenity.name,
          })),
          paymentMethod: {
            description: option.payment_method?.description,
            uuid: option.payment_method?.uuid,
          },
          otherTaxes: parseFloat(option.other_taxes || ""),
          note: option.note,
          checkIn: toDate(option?.check_in as string),
          checkOut: toDate(option?.check_out as string),
          whatsapp: option.whatsapp,
          dailyAmount: option.daily_amount,
        })),
        violatedPolicies: mapViolatedPoliciesDTO(item.violated_policies),
        policyViolationComplement: item.policy_violation_complement,
        policyViolationJustification: item.policy_violation_justification,
        justificationAttachmentUrl: item.justification_attachment_url,
        bookingRules: item.booking_rules?.map(mapHotelRuleDTO),
        paymentMethod: item.payment_method?.uuid ? item.payment_method : undefined,
        paymentProofRecipientName: item.payment_proof_recipient_name,
        paymentProofChannel: item.payment_proof_channel,
        paymentProofSentAt: item.payment_proof_sent_at
          ? toDate(item.payment_proof_sent_at)
          : undefined,
        paymentProofFileUrl: item.payment_proof_file_url,
      }),
    ),
    value: data.value,
    fees: data.fees,
  };
}

export function mapOrderAdvanceItemTypeDTO(
  data?: OrderAdvanceItemTypeDTO,
): OrderAdvanceItemType | null {
  if (!data) return null;
  return {
    advances: data.advances.map(
      (item): OrderAdvanceItem => ({
        uuid: item.uuid,
        advanceNumber: item.advance_number,
        approvalModel: item.approval_model && {
          uuid: item.approval_model.uuid,
          approvers: item.approval_model.approvers.map((data) => ({
            uuid: data.uuid,
            level: data.level,
            name: data.name,
            approvalStatus: data.approval_status,
            isSelfApprover: data.self_approver,
          })),
        },
        approvalRequestDate: item.approval_request_date,
        approvedBy: item.approved_by,
        approvedDate: item.approved_date,
        costCenter: item.cost_center,
        project: item.project,
        phase: item.phase,
        dateOfNeed: item.date_of_need ? toDate(item.date_of_need) : undefined,
        issuer: item.issuer,
        order: item.order,
        branch: item.customer?.branch ? mapBranchDTO(item.customer.branch) : undefined,
        reasonExpense: item.reason_expense
          ? {
              uuid: item.reason_expense.uuid,
              reason: item.reason_expense.reason ?? "",
            }
          : null,
        status: item.status,
        description: item.description,
        createdAt: DateUtils.toDate(item.created_at),
        traveler: mapTravelerDTO(item.traveler),
        value: item.value ?? 0,
        cardTransactions: item.card_transactions
          ? item.card_transactions.map((data) => mapCardTransactionDTO(data))
          : [],
        card: item.card ? mapExpenseCardDTO(item.card) : undefined,
        paymentReceiptUrl: item.payment_receipt_url,
        reasonRejected: item.reason_rejected,
        paymentMethod: item.payment_method,
        pixKey: item.pix_key,
        pixType: item.pix_type as PixKeyType,
        bankInstitution: item.bank_institution,
      }),
    ),
    value: data.value,
    fees: data.fees,
  };
}

export const mapOrderViolatedPolicies = (
  data?: ViolatedPolicyOrderDTO[] | null,
): ViolatedPolicyOrder[] | undefined =>
  data?.map((policy) => ({ message: policy.message, value: policy.policy_value }));

export function mapOrderApprovalModel(data: OrderApprovalModelDTO): OrderApprovalModel {
  return {
    approvalType: data.approval_type,
    approvalRequests: {
      orderId: data.approval_request?.order_uuid,
      branchName: data.approval_request?.branch_name,
      branchId: data.approval_request?.branch_uuid,
      advanceOrderId: data.approval_request?.advance_order_uuid,
      approvalModelId: data.approval_request?.approval_model_uuid,
      approved: data.approval_request?.approved,
    },
    approvers: (data.approvers || []).map(
      (approver): OrderApprover => ({
        uuid: approver?.uuid,
        name: approver?.name,
        email: approver?.email,
        costCenters: approver?.cost_centers,
        projects: approver?.projects,
        phases: approver?.phases,
        approvalStatus: approver?.approval_status,
        isSelfApprover: approver?.self_approver ?? false,
        level: approver?.level,
      }),
    ),
  };
}

export function mapOrderDestinationsDTO(
  data?: OrderDestinationsDTO,
): OrderDestinations | undefined {
  if (!data) return undefined;

  return {
    airwayOrders: (data.airway_orders ?? []).map((item) => ({
      departureAirport: item.departure_airport,
      arrivalAirport: item.arrival_airport,
      departureDate: toDate(item.departure_date),
    })),
    roadOrders: (data.road_orders ?? []).map((item) => ({
      originCity: item.origin_city,
      destinationCity: item.destination_city,
      departureDate: toDate(item.departure_date),
    })),
    hotelOrders: (data.hotel_orders ?? []).map((item) => ({
      hotelName: item.hotel_name,
      city: item.city,
      checkinDate: toDate(item.checkin_date),
      checkoutDate: item.checkout_date ? toDate(item.checkout_date) : undefined,
    })),
    vehicleOrders: (data.vehicle_orders ?? []).map((item) => ({
      category: item.category ?? item.cary ?? "",
      rentalCompany: item.rental_company,
      pickupCity: item.pickup_city,
      dropoffCity: item.dropoff_city,
      pickupDate: toDate(item.pickup_date),
      dropoffDate: item.dropoff_date ? toDate(item.dropoff_date) : undefined,
    })),
  };
}

export function mapOrderDTO(data: OrderDTO): Order {
  const costDivisions = data.cost_divisions
    ?.map((division) => {
      const center = division.allocatable;
      if (!center?.uuid) return null;

      return {
        costCenter: {
          uuid: center.uuid,
          name: center.name,
        },
        percentage: Number(division.percentage),
      };
    })
    .filter(Boolean) as Order["costDivisions"];

  const itemsIncludedDetail = Array.isArray(data.items_included) ? undefined : data.items_included;

  return {
    uuid: data.uuid,
    orderType: data.order_type,
    issuer: data.issuer,
    costCenter: {
      uuid: data.cost_center?.uuid ?? costDivisions?.[0]?.costCenter?.uuid ?? "",
      name: data.cost_center?.name ?? "",
    },
    reasonTrip: data.reason_trip ? mapReasonTripDTO(data.reason_trip) : undefined,
    orderNumber: data.order_number,
    externalOrderNumber: data.external_order_number,
    totalValue: data.total_value,
    totalFees: data.total_fees,
    expirationDate: DateUtils.toDate(data.expires_at),
    isExpired: data.is_expired,
    expenseReportDescription: data.expense_report_description,
    expenseReportName: data.expense_report_name,
    advanceDescription: data.advance_description,
    status: data.status,
    observation: data.obs,
    expenseOnly: data.expense_only,
    travelers: data.travelers?.map(mapTravelerDTO),
    itemsIncluded: (Array.isArray(data.items_included)
      ? data.items_included
      : Object.keys(data.items_included)) as OrderItems[],
    items: {
      [OrderItems.AIRWAY]: mapOrderAirwayItemTypeDTO(itemsIncludedDetail?.airway),
      [OrderItems.ROAD]: mapOrderRoadItemTypeDTO(itemsIncludedDetail?.road),
      [OrderItems.VEHICLE]: mapOrderVehicleItemTypeDTO(itemsIncludedDetail?.vehicle),
      [OrderItems.HOTEL]: mapOrderHotelItemTypeDTO(itemsIncludedDetail?.hotel),
      [OrderItems.ADVANCE]: mapOrderAdvanceItemTypeDTO(itemsIncludedDetail?.advance),
      [OrderItems.HOTEL_OFFLINE]: mapOrderHotelItemTypeDTO(itemsIncludedDetail?.offline_hotel),
      [OrderItems.EXPENSE_REPORT]: mapOrderExpenseReportItemTypeDTO(
        itemsIncludedDetail?.expense_report,
      ),
    },
    destinations: mapOrderDestinationsDTO(data.destinations),
    nearestServiceDate: data.nearest_service_date ? toDate(data.nearest_service_date) : undefined,
    consultant:
      !data.consultant || Array.isArray(data.consultant)
        ? undefined
        : {
            uuid: data.consultant.uuid,
            fullName: data.consultant.full_name,
          },
    approvalModels: (data.approval_models || []).map(mapOrderApprovalModel),
    customer: {
      uuid: data.customer.uuid,
      name: data.customer.name,
      agencyId: data.customer.agency_uuid,
    },
    cancellationTax: {
      road: data.cancellation_tax?.road,
    },
    createdAt: DateUtils.toDate(data.created_at),
    issuedAt: data.issued_at ? DateUtils.toDate(data.issued_at) : undefined,
    linkVoucher: data.link_voucher,
    project: data.project,
    phase: data.phase,
    hasNotifiedExpiration: data.has_notified_expiration,
    orderViolatedPolicies: {
      advance: mapOrderViolatedPolicies(data?.order_violated_policies?.advance),
      airway: mapOrderViolatedPolicies(data?.order_violated_policies?.airway),
      hotel: mapOrderViolatedPolicies(data.order_violated_policies?.hotel),
      road: mapOrderViolatedPolicies(data?.order_violated_policies?.road),
      vehicle: mapOrderViolatedPolicies(data?.order_violated_policies?.vehicle),
      offline_hotel: mapOrderViolatedPolicies(data?.order_violated_policies?.offline_hotel),
    },
    extraCosts: data.extra_costs?.map(mapExtraOrderCostDTO),
    dateOfNeed: data.date_of_need ? DateUtils.toDate(data.date_of_need) : undefined,
    cardInfo: data.card_info ? mapClaraCardDTO(data.card_info) : undefined,
    costDivisions: costDivisions?.length ? costDivisions : undefined,
  };
}

export function mapDuplicityCheckDTO(data: DuplicityCheckDTO): DuplicityCheckResult {
  return {
    canProceed: data.can_proceed,
    orders: data.orders.map(({ order_uuid, order_number }) => ({
      orderId: order_uuid,
      orderNumber: order_number,
    })),
  };
}
